import { useSetupReturnUrlCleanup } from "components/payment/PaymentMethodSetup/setupReturn";
import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { Box, Typography } from "@mui/material";

import { useAuth, useCurrentUser, useOperator } from "../../../globals/hooks";

import AuthenticatedChargeCustomerBlock from "./components/AuthenticatedChargeCustomerBlock";
import UnauthenticatedChargeCustomerBlock from "./components/UnauthenticatedChargeCustomerBlock";
import { LockIcon } from "../../../design-system/icons";
import { grayLight, white } from "../../../design-system/colors";
import PaymentElementChargeCustomerBlock from "./components/PaymentElementChargeCustomerBlock";
import {
  matchesPaymentLinkSetup,
  PaymentLinkSetupContext,
  PaymentLinkSetupReturn,
  readPaymentLinkSetupReturn,
} from "./paymentLinkSetupReturn";

type ChargeCustomerBlockProps = {
  contactId?: string;
  farmAffiliateId?: string;
  totalDue: string;
  isCreatingPayment: boolean;
  onCreatePayment: (paymentMethodId: string, clientSecret?: string) => void;
  companyId?: string;
  paymentSetupContext: PaymentLinkSetupContext;
};

function ChargeCustomerBlock(props: ChargeCustomerBlockProps) {
  const {
    onCreatePayment,
    isCreatingPayment,
    contactId,
    farmAffiliateId,
    totalDue,
    companyId,
    paymentSetupContext,
  } = props;

  // hooks
  const { authStage } = useAuth();
  const { operator } = useOperator();
  const currentUser = useCurrentUser();
  const location = useLocation();
  useSetupReturnUrlCleanup();
  const authenticatedContactId =
    authStage === "authenticated" && !farmAffiliateId
      ? currentUser?.id
      : undefined;
  const eligibleCompanyId =
    authenticatedContactId && companyId === currentUser?.company?.id
      ? companyId
      : undefined;
  const sessionOwner = JSON.stringify([
    operator.id,
    paymentSetupContext,
    location.pathname,
    authStage,
    authenticatedContactId,
  ]);
  const setupReturnRef = useRef<PaymentLinkSetupReturn | null>(null);
  const matchedReturn = readPaymentLinkSetupReturn(
    operator.id,
    paymentSetupContext,
    location.pathname,
    location.search,
    authenticatedContactId
  );
  // Keep the returned setup active while clearing Stripe's URL parameters.
  if (matchedReturn) setupReturnRef.current = matchedReturn;
  const setupReturn =
    setupReturnRef.current &&
    matchesPaymentLinkSetup(
      setupReturnRef.current,
      operator.id,
      paymentSetupContext,
      location.pathname,
      authenticatedContactId
    )
      ? setupReturnRef.current
      : null;
  const inlineContext = {
    ...paymentSetupContext,
    payerId: setupReturn?.payerId,
  };

  const exposureRef = useRef<{ owner: string; enabled: boolean } | null>(null);
  if (
    operator.id &&
    authStage !== "pending" &&
    (authStage !== "authenticated" || currentUser?.id) &&
    exposureRef.current?.owner !== sessionOwner
  ) {
    exposureRef.current = {
      owner: sessionOwner,
      enabled: operator.enablePaymentElement === true,
    };
  }

  // state
  const [selection, setSelection] = useState<{ owner: string; id: string }>();
  const selectedPaymentMethodId =
    selection?.owner === sessionOwner ? selection.id : "";

  // event handlers
  const handleSetPaymentMethod = (selectedPaymentMethodId: string) => {
    setSelection({ owner: sessionOwner, id: selectedPaymentMethodId });
  };

  // cannot login as a farm affiliate
  const isAuthenticated =
    authStage === "authenticated" && !farmAffiliateId && !setupReturn;

  return (
    <>
      <Box
        p={4}
        display="flex"
        flexDirection="column"
        bgcolor={white}
        border={`1px solid ${grayLight}`}
        boxShadow="0px 4px 15px rgba(0, 0, 0, 0.03)"
      >
        {setupReturn ||
        (!isAuthenticated &&
          exposureRef.current?.owner === sessionOwner &&
          exposureRef.current.enabled) ? (
          <>
            <PaymentElementChargeCustomerBlock
              key={JSON.stringify([
                operator.id,
                inlineContext,
                authStage,
                authenticatedContactId,
              ])}
              operatorId={operator.id}
              currency={operator.settings?.currencyCode || "USD"}
              context={inlineContext}
              authenticatedContactId={authenticatedContactId}
              billingDetails={
                authenticatedContactId
                  ? {
                      name: `${currentUser.firstName} ${currentUser.lastName}`,
                      email: currentUser.email,
                      phone: currentUser.mobilePhone,
                    }
                  : undefined
              }
              resume={setupReturn}
              onCreatePayment={onCreatePayment}
              totalDue={totalDue}
              companyId={
                !authenticatedContactId ||
                inlineContext.payerId === eligibleCompanyId
                  ? companyId
                  : undefined
              }
              existingCompanyPaymentMethodIds={currentUser?.company?.paymentMethods?.map(
                (method) => method.id
              )}
              isLoading={isCreatingPayment}
            />
          </>
        ) : isAuthenticated ? (
          <>
            <AuthenticatedChargeCustomerBlock
              key={sessionOwner}
              totalDue={totalDue}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onSetPaymentMethod={handleSetPaymentMethod}
              isLoading={isCreatingPayment}
              onPayClick={() => onCreatePayment(selectedPaymentMethodId)}
              companyId={eligibleCompanyId}
              paymentSetupContext={paymentSetupContext}
              onCreatePayment={onCreatePayment}
            />
            {!operator.enablePaymentElement && (
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <LockIcon size="small" />
                <Box ml={1.5}>
                  <Typography variant="overline">Secure Checkout</Typography>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <UnauthenticatedChargeCustomerBlock
            onCreatePayment={onCreatePayment}
            contactId={contactId}
            farmAffiliateId={farmAffiliateId}
            totalDue={totalDue}
            companyId={companyId}
            isLoading={isCreatingPayment}
          />
        )}
      </Box>
    </>
  );
}

export default ChargeCustomerBlock;
